import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  InputAdornment,
  IconButton,
  Box
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { memo, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import LabeledInput from "../../../components/common/fields/LabeledInput";

const ACCENT_COLOR = '#ba2156';

const adornment = (position: 'start' | 'end', node: ReactNode) => (
  <InputAdornment position={position}>{node}</InputAdornment>
);

const visibilityToggle = (visible: boolean, onToggle: () => void, label: string) => (
  <IconButton onClick={onToggle} edge="end" aria-label={label}>
    {visible ? <VisibilityOff /> : <Visibility />}
  </IconButton>
);

interface AccountSettingsSectionProps {
  email: string;
  password: string;
  confirmPassword: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  isEdit?: boolean;
}

const AccountSettingsSectionComponent = ({
  email,
  password,
  confirmPassword,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  isEdit = false,
}: AccountSettingsSectionProps) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMatch = password === confirmPassword;
  const showPasswordMismatch = confirmPassword.length > 0 && !passwordsMatch;

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<Lock sx={{ color: '#ba2156' }} />}
        title={
          <Typography variant="h6" component="h3">
            {t("salon.settings.account.title")}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {t("salon.settings.account.subtitle")}
          </Typography>
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <LabeledInput
            label={t("salon.settings.account.email")}
            type="email"
            placeholder={t("salon.settings.account.email_placeholder")}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={isEdit}
            startAdornment={adornment('start', <Email sx={{ color: ACCENT_COLOR }} />)}
          />
          <LabeledInput
            label={t("salon.settings.account.password")}
            type={showPassword ? 'text' : 'password'}
            placeholder={t("salon.settings.account.password_placeholder")}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            startAdornment={adornment('start', <Lock sx={{ color: ACCENT_COLOR }} />)}
            endAdornment={adornment('end', visibilityToggle(showPassword, () => setShowPassword(!showPassword), t("salon.settings.account.toggle_password")))}
          />
          <LabeledInput
            label={t("salon.settings.account.confirm_password")}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t("salon.settings.account.confirm_password_placeholder")}
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            error={showPasswordMismatch}
            helperText={showPasswordMismatch ? t("salon.settings.validation.password_mismatch") : ''}
            startAdornment={adornment('start', <Lock sx={{ color: ACCENT_COLOR }} />)}
            endAdornment={adornment('end', visibilityToggle(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), t("salon.settings.account.toggle_confirm_password")))}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export const AccountSettingsSection = memo(AccountSettingsSectionComponent);
